/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from 'react';
import { formatRecordingTime } from '../ai/shared/TimeFormatter';
import { useSpeechToTextApi } from './SpeechToTextApi';
import { useNotification } from '../../components/Notification/NotificationContext';

export const AudioRecorder = ({
    onMessageChange,
    onTextInserted,
    messageMaxLength,
    isLoading,
    recordingBtnText = "Записать сообщение",
    targetFieldClass = 'audio-recorder-target',
    defaultTargetFieldId = 'message'
}) => {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [audioFileName, setAudioFileName] = useState('');
    const [currentMaxRecordingTime, setCurrentMaxRecordingTime] = useState(300);
    const {
        showError,
        showWarning,
        showInfo
    } = useNotification();

    const isCancellingRef = useRef(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const messageBeforeRecordingRef = useRef('');
    const wasManuallyCancelledRef = useRef(false);
    const { submitSpeechRequest, cancelSpeechRequest } = useSpeechToTextApi();
    const cancelSpeechRequestRef = useRef(cancelSpeechRequest);
    const recordingTargetRef = useRef({
        field: null,
        cursorStart: 0,
        cursorEnd: 0,
        fieldId: ''
    });

    const MAX_RECORDING_TIMES = {
        10000: 300,
        5000: 240,
        2000: 180,
        1000: 120,
        500: 60,
        250: 30,
        100: 10,
        default: 300
    };

    useEffect(() => {
        cancelSpeechRequestRef.current = cancelSpeechRequest;
    }, [cancelSpeechRequest]);

    const generateGuid = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    };

    const getActiveFieldAndMaxTime = () => {
        const activeElement = document.activeElement;
        let targetField = null;
        let maxRecordingTime = MAX_RECORDING_TIMES.default;
        let cursorStart = 0;
        let cursorEnd = 0;

        if (activeElement && activeElement.classList.contains(targetFieldClass)) {
            targetField = activeElement;
        } else {
            targetField = document.getElementById(defaultTargetFieldId);
        }

        if (targetField && targetField.hasAttribute('contenteditable')) {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                if (targetField.contains(range.startContainer)) {
                    const tempRange = document.createRange();
                    tempRange.selectNodeContents(targetField);
                    tempRange.setEnd(range.startContainer, range.startOffset);
                    cursorStart = tempRange.toString().length;

                    tempRange.setEnd(range.endContainer, range.endOffset);
                    cursorEnd = tempRange.toString().length;
                }
            }
        } else if (targetField) {
            cursorStart = targetField.selectionStart || 0;
            cursorEnd = targetField.selectionEnd || 0;
        }

        if (targetField) {
            const maxLength = parseInt(targetField.getAttribute('maxlength')) || messageMaxLength;

            const lengthKeys = Object.keys(MAX_RECORDING_TIMES)
                .filter(key => key !== 'default')
                .map(Number)
                .sort((a, b) => b - a);

            for (const key of lengthKeys) {
                if (maxLength >= key) {
                    maxRecordingTime = MAX_RECORDING_TIMES[key];
                    break;
                }
            }
        }

        return {
            targetField,
            maxRecordingTime,
            cursorStart,
            cursorEnd
        };
    };

    const lockTargetField = (field) => {
        if (field) {
            if (field.hasAttribute('contenteditable')) {
                field.setAttribute('contenteditable', 'false');
            } else {
                field.setAttribute('readonly', 'true');
            }
            field.style.opacity = '0.7';
            field.style.cursor = 'not-allowed';
        }
    };

    const unlockTargetField = () => {
        const fields = document.querySelectorAll(`.${targetFieldClass}`);
        fields.forEach(field => {
            if (field.hasAttribute('contenteditable')) {
                field.setAttribute('contenteditable', 'true');
            } else {
                field.removeAttribute('readonly');
            }
            field.style.opacity = '';
            field.style.cursor = '';
        });
    };

    const startRecording = async () => {
        try {
            const { targetField, maxRecordingTime, cursorStart, cursorEnd } = getActiveFieldAndMaxTime();

            if (!targetField) {
                showError('Не найдено поле для ввода текста');
                return;
            }

            setCurrentMaxRecordingTime(maxRecordingTime);

            recordingTargetRef.current = {
                field: targetField,
                cursorStart: cursorStart,
                cursorEnd: cursorEnd,
                fieldId: targetField.id
            };

            lockTargetField(targetField);

            isCancellingRef.current = false;
            messageBeforeRecordingRef.current = targetField.hasAttribute('contenteditable')
                ? targetField.textContent
                : targetField.value;
            const newGuid = generateGuid();
            setProcessingId(newGuid);

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (!isCancellingRef.current) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = async () => {
                if (isCancellingRef.current) {
                    unlockTargetField();
                    return;
                }

                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
                await sendRecordingToBackend(audioBlob, newGuid);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start(1000);
            setIsRecording(true);
            setRecordingTime(0);

            timerRef.current = setInterval(() => {
                setRecordingTime(prev => {
                    const newTime = prev + 1;
                    if (newTime >= maxRecordingTime) {
                        stopRecording();
                        return maxRecordingTime;
                    }
                    return newTime;
                });
            }, 1000);

        } catch (error) {
            console.error('Error starting recording:', error);
            showError('Не удалось получить доступ к микрофону');
            unlockTargetField();
        }
    };

    const stopRecording = () => {
        try {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }

            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();

                if (mediaRecorderRef.current.stream) {
                    mediaRecorderRef.current.stream.getTracks().forEach(track => {
                        track.stop();
                        track.enabled = false;
                    });
                }

                setIsRecording(false);
                setIsProcessing(true);
            }
        } catch (error) {
            console.error('Error stopping recording:', error);
            showError('Ошибка при остановке записи');
            setIsRecording(false);
            setIsProcessing(false);
            unlockTargetField();
        }
    };

    const cancelRecording = (e) => {
        e.preventDefault();

        try {
            isCancellingRef.current = true;
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }

            if (mediaRecorderRef.current) {
                if (mediaRecorderRef.current.state !== 'inactive') {
                    mediaRecorderRef.current.stop();
                }

                if (mediaRecorderRef.current.stream) {
                    mediaRecorderRef.current.stream.getTracks().forEach(track => {
                        track.stop();
                        track.enabled = false;
                    });
                }
            }

            audioChunksRef.current = [];
            setIsRecording(false);
            setIsProcessing(false);
            setRecordingTime(0);
            setProcessingId(null);

            if (recordingTargetRef.current.field && messageBeforeRecordingRef.current !== null) {
                const event = {
                    target: {
                        value: messageBeforeRecordingRef.current,
                        id: recordingTargetRef.current.fieldId
                    }
                };
                onMessageChange(event);
            }

            unlockTargetField();
        } catch (error) {
            console.error('Error canceling recording:', error);
            showError('Ошибка при отмене записи');
            unlockTargetField();
        }
    };

    const cancelProcessing = async () => {
        if (!processingId || !audioFileName) return false;

        try {
            await cancelSpeechRequest(processingId, audioFileName);
            setIsProcessing(false);
            setProcessingId(null);
            setAudioFileName('');
            showInfo('Обработка аудио отменена');
            unlockTargetField();
            return true;
        } catch (error) {
            console.error('Error canceling processing:', error);
            showError(error.message || 'Ошибка при отмене обработки аудио');
            return false;
        }
    };

    const handleManualCancel = async (e) => {
        e.preventDefault();

        wasManuallyCancelledRef.current = true;
        const success = await cancelProcessing();
        if (!success) {
            wasManuallyCancelledRef.current = false;
        }
    };

    const sendRecordingToBackend = async (audioBlob, guid) => {
        try {
            if (isCancellingRef.current) {
                unlockTargetField();
                return;
            }

            if (audioBlob.size > 50_000_000) {
                showError('Размер аудиофайла превышает допустимый лимит (50MB)');
                unlockTargetField();
                return;
            }

            const fileName = `recording_${new Date().toISOString().replace(/[:.]/g, '-')}.wav`;
            setAudioFileName(fileName);

            setIsProcessing(true);

            try {
                const recognizedText = await submitSpeechRequest(audioBlob, guid, fileName);
                let newValue;

                if (recognizedText && recognizedText.trim()) {
                    const { field, cursorStart, cursorEnd, fieldId } = recordingTargetRef.current;
                    let targetField = field;

                    if (!targetField || !document.body.contains(targetField)) {
                        targetField = document.getElementById(fieldId);
                        if (!targetField) {
                            showError('Целевое поле для ввода не найдено', 'error');
                            return;
                        }
                    }

                    let newValue = '';

                    if (targetField.hasAttribute('contenteditable')) {
                        const selection = window.getSelection();

                        if (selection.rangeCount > 0 && targetField.contains(selection.anchorNode)) {
                            const range = selection.getRangeAt(0);
                            range.deleteContents();
                            const textNode = document.createTextNode(recognizedText);
                            range.insertNode(textNode);

                            const newRange = document.createRange();
                            newRange.setStart(textNode, textNode.length);
                            newRange.collapse(true);
                            selection.removeAllRanges();
                            selection.addRange(newRange);

                            newValue = targetField.textContent;
                        } else {
                            const range = document.createRange();
                            const walker = document.createTreeWalker(targetField, NodeFilter.SHOW_TEXT, null);

                            let currentOffset = 0;
                            let startNode = null;
                            let startOffset = 0;
                            let found = false;

                            let node;
                            while ((node = walker.nextNode())) {
                                const nodeLength = node.textContent.length;

                                if (cursorStart <= currentOffset + nodeLength && !found) {
                                    startNode = node;
                                    startOffset = Math.min(cursorStart - currentOffset, nodeLength);
                                    found = true;
                                }
                                currentOffset += nodeLength;
                            }

                            if (startNode) {
                                range.setStart(startNode, startOffset);

                                if (cursorEnd > cursorStart) {
                                    let endNode = null;
                                    let endOffset = 0;
                                    currentOffset = 0;
                                    found = false;
                                    walker.currentNode = targetField;

                                    while ((node = walker.nextNode())) {
                                        const nodeLength = node.textContent.length;

                                        if (cursorEnd <= currentOffset + nodeLength && !found) {
                                            endNode = node;
                                            endOffset = Math.min(cursorEnd - currentOffset, nodeLength);
                                            found = true;
                                            break;
                                        }
                                        currentOffset += nodeLength;
                                    }

                                    if (endNode) {
                                        range.setEnd(endNode, endOffset);
                                    } else {
                                        range.setEnd(startNode, startOffset);
                                    }
                                } else {
                                    range.setEnd(startNode, startOffset);
                                }

                                range.deleteContents();
                                const textNode = document.createTextNode(recognizedText);
                                range.insertNode(textNode);

                                const newRange = document.createRange();
                                newRange.setStart(textNode, textNode.length);
                                newRange.collapse(true);
                                const newSelection = window.getSelection();
                                newSelection.removeAllRanges();
                                newSelection.addRange(newRange);

                                newValue = targetField.textContent;
                            } else {
                                targetField.appendChild(document.createTextNode(recognizedText));
                                newValue = targetField.textContent;

                                const range = document.createRange();
                                range.selectNodeContents(targetField);
                                range.collapse(false);
                                const selection = window.getSelection();
                                selection.removeAllRanges();
                                selection.addRange(range);
                            }
                        }
                    } else {
                        const currentValue = targetField.value || '';

                        newValue =
                            currentValue.substring(0, cursorStart) +
                            recognizedText +
                            currentValue.substring(cursorEnd, currentValue.length);

                        const event = {
                            target: {
                                value: newValue,
                                id: targetField.id
                            }
                        };

                        onMessageChange(event);

                        setTimeout(() => {
                            if (targetField && document.body.contains(targetField)) {
                                targetField.focus();
                                targetField.setSelectionRange(
                                    cursorStart + recognizedText.length,
                                    cursorStart + recognizedText.length
                                );
                            }
                        }, 0);
                    }

                    if (onTextInserted) {
                        onTextInserted();
                    }

                    if (newValue && newValue.length > messageMaxLength) {
                        showWarning(`Превышен лимит в ${messageMaxLength} символов. Сократите текст перед отправкой.`);
                    }

                    if (onMessageChange && !targetField.hasAttribute('contenteditable')) {
                        onMessageChange({
                            target: {
                                value: newValue,
                                id: targetField.id
                            }
                        });
                    }
                }

                if (newValue && newValue.length > messageMaxLength) {
                    showWarning(`Превышен лимит в ${messageMaxLength} символов. Сократите текст перед отправкой.`);
                }
            } catch (error) {
                if (wasManuallyCancelledRef.current) {
                    return;
                }

                console.error('Error processing audio:', error);
                showError(error.message || 'Ошибка обработки аудио');

                if (recordingTargetRef.current.field && messageBeforeRecordingRef.current !== null) {
                    const event = {
                        target: {
                            value: messageBeforeRecordingRef.current,
                            id: recordingTargetRef.current.fieldId
                        }
                    };
                    onMessageChange(event);
                }
            }
        } catch (error) {
            console.error('Error in sendRecordingToBackend:', error);
            showError('Ошибка при отправке аудио на сервер');
        } finally {
            setIsProcessing(false);
            setProcessingId(null);
            setAudioFileName('');
            messageBeforeRecordingRef.current = '';
            unlockTargetField();
            wasManuallyCancelledRef.current = false;
        }
    };

    const handleButtonClick = (e) => {
        e.preventDefault();

        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    useEffect(() => {
        return () => {
            if (mediaRecorderRef.current && isRecording) {
                mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
            }
            clearInterval(timerRef.current);

            if (isProcessing && processingId) {
                cancelSpeechRequestRef.current(processingId).catch(console.error);
            }

            unlockTargetField();
        };
    }, []);

    return (
        <div className="voice-recording-container">
            {isRecording ? (
                <div className="recording-indicator">
                    <span className="recording-dot"></span>
                    <span>Идет запись: {formatRecordingTime(recordingTime)} / {formatRecordingTime(currentMaxRecordingTime)}</span>
                    <button
                        type="button"
                        className="voice-cancel-button custom-button dt1520-button active-dt1520-button view-button ml-2"
                        onMouseDown={cancelRecording}
                        disabled={isLoading}>
                        Отменить
                    </button>
                </div>
            ) : isProcessing ? (
                <div className="processing-indicator">
                    <span className="processing-spinner"></span>
                    <span>Обработка аудио...</span>
                    <button
                        type="button"
                        className="voice-cancel-button custom-button dt1520-button active-dt1520-button view-button"
                        onMouseDown={handleManualCancel}
                        disabled={!processingId}>
                        Отменить
                    </button>
                </div>
            ) : null}

            {!isProcessing ? (
                <button
                    type="button"
                    className="voice-record-button custom-button dt1520-button active-dt1520-button view-button"
                    onMouseDown={handleButtonClick}
                    disabled={isLoading || isProcessing}
                >
                    {isRecording ? 'Остановить запись' : recordingBtnText}
                </button>
            ) : null}

            {!isRecording && !isProcessing ? (
                <div className="recording-hint">
                    Нажмите кнопку для начала записи. Поле будет заблокировано на время записи.
                </div>
            ) : null}
        </div>
    );
};